import React, { useState, useEffect } from "react";
import { 
  HStack, 
  Button, 
  Divider, 
  VStack,
  Tooltip,
  Spinner,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { 
  FaDownload, 
  FaComments, 
  FaUndo, 
  FaQuestionCircle,
  FaSave,
  FaFolder
} from "react-icons/fa";
import { useRequestFormModal } from "./RequestFormModal";
import SaveSlotModal from "./SaveSlotModal";
import LoadSlotModal from "./LoadSlotModal";
import { 
  loadSaveSlotsSafe, 
  saveSettingsToSlotSafe, 
  setActiveSlotSafe, 
  verifyCurrentUser 
} from "../../services/userSettingsService";

const ActionButtons = ({ 
  settings,
  defaultSettings,
  setSettings,
  setHeightMultiplier,
  setWidthMultiplier,
  setActivePreset,
  setShowGuide,
  accentColor,
  updateSettingsUrl,
  setSaveStatus,
  isUploading
}) => {
  // Use the shared request form modal
  const { onOpen, RequestFormModalComponent } = useRequestFormModal(settings);
  const toast = useToast();
  
  // Disclosures for save and load modals
  const { 
    isOpen: isSaveModalOpen, 
    onOpen: onOpenSaveModal, 
    onClose: onCloseSaveModal 
  } = useDisclosure();
  
  const { 
    isOpen: isLoadModalOpen, 
    onOpen: onOpenLoadModal, 
    onClose: onCloseLoadModal 
  } = useDisclosure();
  
  // States
  const [slotData, setSlotData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Load existing save slots when component mounts
  useEffect(() => {
    const fetchSlots = async () => {
      setIsInitializing(true);
      try {
        // Verify the current user first
        const userStatus = await verifyCurrentUser();
        
        if (!userStatus.valid) {
          toast({
            title: "Authentication Error",
            description: "Please refresh the page and try again.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          setIsInitializing(false);
          return;
        }
        
        // If user has changed, notify
        if (userStatus.changed) {
          toast({
            title: "User Session Changed",
            description: "Loading your saved configurations...",
            status: "info",
            duration: 3000,
            isClosable: true,
          });
        }
        
        // Load save slots using the safe version
        const result = await loadSaveSlotsSafe();
        if (result.success) {
          setSlotData(result.slotData);
        }
      } catch (err) {
        console.error("Error loading save slots:", err);
        toast({
          title: "Error Loading Data",
          description: "Could not load your saved configurations.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsInitializing(false);
      }
    };
    
    fetchSlots();
  }, [toast]);

  const resetToDefaultSettings = () => {
    setSettings({...defaultSettings});
    setHeightMultiplier(defaultSettings.widgetHeightMultiplier || 1);
    setWidthMultiplier(defaultSettings.widgetWidthMultiplier || 1);
    setActivePreset("custom");
    localStorage.setItem('settings', JSON.stringify(defaultSettings));
    updateSettingsUrl(defaultSettings);
    
    setSaveStatus('Reset to defaults');
    setTimeout(() => setSaveStatus(''), 2000);
  };

  const handlePreview = () => {
    localStorage.setItem('previewSettings', JSON.stringify(settings));
    const newWindow = window.open('/preview', '_blank', 'noopener');
    if (newWindow) newWindow.opener = null;
  };
  
  const handleSaveToSlot = async (slotNumber, slotName) => {
    setIsSaving(true);
    setSaveStatus(`Saving to "${slotName}"...`);
    
    try {
      // Use the safe version that verifies user identity first
      const result = await saveSettingsToSlotSafe(settings, slotNumber, slotName, true);
      
      if (result.success) {
        setSlotData(result.slotData);
        setSaveStatus(result.message);
        onCloseSaveModal();
      } else {
        setSaveStatus(`Error: ${result.message || 'Failed to save'}`);
        toast({
          title: "Save Error",
          description: result.message || "Failed to save configuration",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error saving to slot:", err);
      setSaveStatus("Error: Failed to save configuration");
      toast({
        title: "Save Error",
        description: "An unexpected error occurred while saving.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const handleLoadFromSlot = async (slotNumber) => {
    setIsLoading(true);
    setSaveStatus("Loading configuration...");
    
    try {
      // Use the safe version that verifies user identity first
      const result = await setActiveSlotSafe(slotNumber, true);
      
      if (result.success) {
        // Apply the loaded settings
        setSettings(result.settings);
        setSlotData(result.slotData);
        
        if (result.settings.widgetHeightMultiplier) {
          setHeightMultiplier(result.settings.widgetHeightMultiplier);
        }
        
        if (result.settings.widgetWidthMultiplier) {
          setWidthMultiplier(result.settings.widgetWidthMultiplier);
        }
        
        setSaveStatus(result.message);
        onCloseLoadModal();
      } else {
        setSaveStatus(`Error: ${result.message || 'Failed to load'}`);
        toast({
          title: "Load Error",
          description: result.message || "Failed to load configuration",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error loading from slot:", err);
      setSaveStatus("Error: Failed to load configuration");
      toast({
        title: "Load Error",
        description: "An unexpected error occurred while loading.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  return (
    <>
      <Divider my={2} />
      
      <VStack spacing={4} align="stretch">
        <HStack spacing={4}>
          <Button 
            leftIcon={<FaDownload />} 
            onClick={onOpen}
            colorScheme="green" 
            size="md"
            flex={1}
          >
            Request Chatbot
          </Button>
          <Button 
            leftIcon={<FaComments />}
            onClick={handlePreview}
            colorScheme="blue"
            size="md"
            flex={1}
          >
            Preview
          </Button>
        </HStack>

        <HStack spacing={4} mt={2}>
          <Button 
            leftIcon={<FaUndo />} 
            onClick={resetToDefaultSettings} 
            colorScheme="gray" 
            size="md"
            variant="outline"
            flex={1}
          >
            Reset to Default
          </Button>
          <Button 
            leftIcon={<FaQuestionCircle />} 
            onClick={() => setShowGuide(true)} 
            colorScheme="orange" 
            size="md"
            variant="outline"
            flex={1}
          >
            Guide
          </Button>
        </HStack>
        
        <HStack spacing={4} mt={2}>
          <Tooltip label="Save your widget configuration">
            <Button 
              leftIcon={isInitializing ? <Spinner size="sm" /> : <FaSave />}
              onClick={onOpenSaveModal}
              colorScheme="teal"
              size="md"
              variant="outline"
              flex="1"
              isDisabled={isInitializing || isSaving || isUploading}
            >
              Save
            </Button>
          </Tooltip>
          
          <Tooltip label="Load a saved widget configuration">
            <Button 
              leftIcon={isInitializing ? <Spinner size="sm" /> : <FaFolder />}
              onClick={onOpenLoadModal}
              colorScheme="purple"
              size="md"
              variant="outline"
              flex="1"
              isDisabled={isInitializing || isLoading}
            >
              Load
            </Button>
          </Tooltip>
        </HStack>
      </VStack>

      {/* Save Slot Modal */}
      <SaveSlotModal
        isOpen={isSaveModalOpen}
        onClose={onCloseSaveModal}
        onSave={handleSaveToSlot}
        slotData={slotData}
        isSaving={isSaving}
        accentColor={accentColor}
      />
      
      {/* Load Slot Modal - add setSlotData prop */}
      <LoadSlotModal
        isOpen={isLoadModalOpen}
        onClose={onCloseLoadModal}
        onLoad={handleLoadFromSlot}
        slotData={slotData}
        setSlotData={setSlotData}
        isLoading={isLoading}
        accentColor={accentColor}
      />

      {/* Render the request form modal */}
      <RequestFormModalComponent accentColor={accentColor} />
    </>
  );
};

export default ActionButtons;